import { injectIntl } from '../../node_modules/react-intl/index'
import { requireAuthentication } from '../helpers/authentication'
import { useEffect, useState } from 'react'

import { useUserPlant } from '../hooks/User'
import { IssueEditForm } from './IssueEditForm'
import { IssueSummary } from './IssueSummary'
import { useLocation } from 'react-router-dom'

import { Checkbox, Modal, Flex, ConfigProvider } from 'antd'
import { WHITE_COLOR, AZURE_RADIANCE_COLOR } from '../constants'

import './styles.scss'
import { isNull } from 'lodash'
import url from '../services/url'
import { buildFaultDescription } from './utils'

const getFaultIdentifier = (fault) => {
  let identifier = null
  if (fault['type'] == 'component') {
    identifier = fault['component_id']
  } else if (fault['type'] == 'harness') {
    identifier = fault['harness']
  } else if (fault['type'] == 'device') {
    identifier = fault['device_name']
  }
  return identifier
}

const getIdentifierKey = (fault) => {
  if (fault['type'] == 'component') {
    return 'component_id'
  }
  if (fault['type'] == 'harness') {
    return 'harness_id'
  }
  if (fault['type'] == 'device') {
    return 'device_name'
  }
  return ''
}

const storageKey = 'incident-mgmt-faults'

const allProblemsSameMakeModel = (fault) => {
  if (!fault || !fault.problems || !fault.make_model_id) {
    return true
  }
  const mmID = fault.make_model_id
  return fault.problems.every((flt) => flt.make_model_id === mmID)
}

const loadFaultsFromLocalStorage = () => {
  let res = {}
  const faultString = localStorage.getItem(storageKey)
  if (faultString) {
    res = JSON.parse(faultString)
  }
  return res
}

// the fault will be base64/url encoded as a query param
// for use with the "Add Issue" and "n Issues" button
export const navigateToIncidentMgmtPage = (fault) => {
  let allFaults = loadFaultsFromLocalStorage()
  allFaults[fault.id] = fault
  const faultStr = JSON.stringify(allFaults)
  localStorage.setItem(storageKey, faultStr)
  const url = `${window.location.origin}/incidents?tab=incident-management&fault=${fault.id}`
  window.open(url, '_self')
}

const isFaultInUrl = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const faultId = urlParams.get('fault')
  if (!faultId) {
    return false
  }
  const allFaults = loadFaultsFromLocalStorage()
  if (!allFaults) {
    return false
  }
  return allFaults.hasOwnProperty(faultId)
}

const getFaultFromURL = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const faultId = urlParams.get('fault')
  let fault = null
  if (faultId) {
    const allFaults = loadFaultsFromLocalStorage()
    if (allFaults.hasOwnProperty(faultId)) {
      fault = allFaults[faultId]
    } else if (allFaults.hasOwnProperty('id') && allFaults['id'] == faultId) {
      fault = allFaults
    }
  }
  return fault
}

const emptyIssue = {
  component_id: null, // ID of component, harness, or device
  fault_type: null,
  fault_short_code: null,
  plant_id: null,
  issue_category_id: null,
  problem_log_id: null, // id from problem_logs.id
  description: null,
  responsible: null,
  customer_reference: null,
  problem_description: {
    what: null,
    when: null,
    where: null,
    who: null,
    which: null,
  },
  possible_root_causes: {
    man: null,
    machine: null,
    method: null,
    material: null,
    design: null,
  },
  direct_root_causes: {
    five_whys: [null, null, null, null, null],
    direct_root_cause: null,
    detection_root_cause: null,
    systemic_root_cause: null,
  },
  actions: [
    {
      assignee: null,
      type: null,
      description: null,
      start_date: null,
      due_date: null,
      close_date: null,
      status: 'open',
    },
  ],
  status: 'open',
}

// Gets the issue we're editing based on the passed-in fault
const getIssue = (fault, plant_id) => {
  if (!!fault) {
    if (!!fault.attachedIssue) {
      // the fault already has a linked issue
      return fault.attachedIssue
    }
    // We need to create the issue with the fault info added
    const issue = structuredClone(emptyIssue)
    issue['fault_type'] = fault['problem_code'] //yeah this is a bit weird
    const identifier = getFaultIdentifier(fault)
    const identifierKey = getIdentifierKey(fault)
    issue[identifierKey] = identifier
    issue['plant_id'] = plant_id
    issue['problem_log_id'] = fault['id']
    issue['fault_short_code'] = fault['short_code']
    issue['description'] = buildFaultDescription(fault)

    return issue
  }
  return null
}

// Gets the possibly related issues to the fault
const getPotentialIssues = (fault) => {
  if (fault) {
    return fault['relatedIssues']
  }
  return null
}

// this page will be opened with a base64-encoded fault as an
// optional "fault" url query param - if it exists we're here from an
// "add issue" or "n issues" button, if not we're here from the home page
function IncidentMgmtPage({ intl }) {
  const { search } = useLocation()
  const searchParams = new URLSearchParams(search)
  const faultParam = searchParams.get('fault')

  const [userPlant] = useUserPlant()
  // if fault is null we're just here from the main page,
  // if it's populated we're here from an add/edit issue button
  const [faultInUrl, setFaultInUrl] = useState(false)
  const [fault, setFault] = useState(null)
  // if issue is null we are not editing an issue yet but either
  // potentially adding a case to an issue or just viewing from the
  // main page. If issue is populated we're adding/editing it
  const [issue, setIssue] = useState(null)
  // potentialIssues are all of the ones that the fault may be linked
  // to where we might add a case
  const [potentialIssues, updatePotentialIssues] = useState(null)

  useEffect(() => {
    const faultTemp = getFaultFromURL()
    const potentialIssuesTemp = getPotentialIssues(faultTemp)
    setFaultInUrl(faultParam ? faultParam : false)
    setFault(faultTemp)
    setIssue(getIssue(faultTemp, userPlant))
    updatePotentialIssues(potentialIssuesTemp)
  }, [faultParam])

  const issueParam = searchParams.get('issue')

  // controls the modal to pick vins if we have multiple make model IDs
  const [isChoosingVINs, setIsChoosingVINs] = useState(false)
  const [vinsToMMIDs, setVinsToMMIDs] = useState({})
  const [selectedMMID, setSelectedMMID] = useState(null)
  const [selectedVINs, setSelectedVINs] = useState([])

  useEffect(() => {
    if (!allProblemsSameMakeModel(fault)) {
      let newVinsToMMIDs = {}
      if (fault.problems) {
        fault.problems.forEach((prb) => {
          newVinsToMMIDs[prb.vin] = prb.make_model_id
        })
      }
      setVinsToMMIDs(newVinsToMMIDs)
      setIsChoosingVINs(true)
    } else {
      setIsChoosingVINs(false)
    }
  }, [fault])

  const vinSelectOK = () => {
    const probs = fault.problems.filter(
      (prb) => prb.make_model_id === selectedMMID,
    )
    const newFault = structuredClone(probs[0])
    newFault.problems = probs
    setFault(newFault)
    setIsChoosingVINs(false)
  }

  const vinSelectCancel = () => {
    setIsChoosingVINs(false)
    window.history.go(-1)
  }

  const selectVIN = (selected, makeModelID, VIN) => {
    let newSelectedVINs = structuredClone(selectedVINs)
    if (selected) {
      // add it to the list
      newSelectedVINs.push(VIN)
    } else {
      // remove it from the list
      newSelectedVINs = newSelectedVINs.filter((v) => v !== VIN)
    }
    if (newSelectedVINs.length > 0 && selectedMMID === null) {
      setSelectedMMID(makeModelID)
    } else if (newSelectedVINs.length === 0) {
      setSelectedMMID(null)
    }
    setSelectedVINs([...new Set(newSelectedVINs)])
  }

  const isEditingIssue = () => {
    return faultInUrl && (!potentialIssues || potentialIssues.length === 0)
  }

  const isViewingIssue = () => {
    return !faultInUrl || (!!potentialIssues && potentialIssues.length > 0)
  }

  return (
    <Flex vertical={false} id="incident-mgmt-page" gap={20}>
      <ConfigProvider
        theme={{
          components: {
            // The branded Tabs background has no app-wide token equivalent,
            // so it's overridden here; everything else (Card, Collapse,
            // Table, Input, Button, Select) now comes from the app-wide
            // AntConfigProvider/themeComponentCreator so this page stays in
            // sync with the global light/dark toggle.
            Tabs: {
              colorBgContainer: AZURE_RADIANCE_COLOR,
              itemSelectedColor: WHITE_COLOR,
              horizontalMargin: '0 0 10px 0',
            },
          },
        }}
      >
        <div className="incident-mgmt-left-container">
          {isEditingIssue() && ( // we want to be able to edit
            <IssueEditForm
              issue={issue}
              setIssue={setIssue}
              fault={fault}
              intl={intl}
              setFault={setFault}
            />
          )}
          {isViewingIssue() && ( // we just want to see the details
            <IssueEditForm
              fault={fault}
              showFaultDetails={true}
              issue={issue}
              setIssue={setIssue}
              orientation="vertical"
              readOnly={true}
              intl={intl}
              setFault={setFault}
            />
          )}
        </div>
        <div className="incident-mgmt-right-container">
          <IssueSummary
            fault={fault}
            issue={issue}
            intl={intl}
            setIssue={setIssue}
            potentialIssues={potentialIssues}
            setFault={setFault}
            showList={isViewingIssue()}
            defaultIssueId={issueParam}
          />
        </div>
        <Modal
          open={isChoosingVINs}
          title="Select VINs"
          onOk={vinSelectOK}
          onCancel={vinSelectCancel}
        >
          Select VINs
          {Object.keys(vinsToMMIDs).map((vin) => {
            const mmID = vinsToMMIDs[vin]
            return (
              <p>
                <Checkbox
                  disabled={!(isNull(selectedMMID) || mmID === selectedMMID)}
                  onChange={(e) => selectVIN(e.target.checked, mmID, vin)}
                >
                  {vin}
                </Checkbox>
              </p>
            )
          })}
        </Modal>
      </ConfigProvider>
    </Flex>
  )
}

export default requireAuthentication(injectIntl(IncidentMgmtPage))
